import React, {createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef, useMemo} from "react";
import useWebSocket, {ReadyState} from "react-use-websocket";
import {useAppSelector} from "hooks/useAppSelector";
import useMessage from "../hooks/useMessage";
import {ChatList, ChatListItem, UserInfo, WebSocketContextType, WebSocketMessage} from "../types";
import {useDispatch} from "react-redux";
import apiUser from "app/api/user";
import {setBalance, getUserTurnover, updateUserInfo} from "app/store/slices/user";

interface WebSocketProviderProps {
    children: ReactNode;
    initialUrl?: string | null;
    heartbeatInterval?: number; // 心跳间隔，单位毫秒
    heartbeatMessage?: string; // 心跳消息内容
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({children, initialUrl = null, heartbeatInterval = 30000, heartbeatMessage = "?"}) => {
    const {handleReceiveMsg} = useMessage();
    const isClient = typeof window !== "undefined";
    const token = useAppSelector((state) => state.user.token);
    const dispatch = useDispatch();
    const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [messageQueue, setMessageQueue] = useState<WebSocketMessage[]>([]);
    const [wsUrl, setWsUrl] = useState<string | null>(initialUrl);
    const [listChat, setListChat] = useState<ChatListItem[]>([]);
    const [user, setUser] = useState<UserInfo | undefined>();
    const [lastJsonMsg, setLastJsonMsg] = useState<WebSocketMessage | null>(null);
    const [listChannelBans, setListChannelBans] = useState<number[]>([]);
    const fetchAndUpdateBalance = useCallback(async () => {
        try {
            const {status, data} = await apiUser.getBalance();
            if (status === 200 && data.code === 0) {
                try {
                    const result = JSON.parse(data.data);
                    const list = result?.data || [];
                    dispatch(setBalance(list));
                    dispatch(getUserTurnover());
                } catch (e) {}
            }
        } catch (e) {}
    }, [dispatch]);
    const {sendMessage, readyState} = useWebSocket(wsUrl, {
        onMessage: (event) => {
            // ping message response is "."
            if (event.data === ".") {
                return;
            }
            try {
                const parsed = JSON.parse(event.data);
                setLastJsonMsg(parsed as WebSocketMessage);
                setMessageQueue((prev) => [...prev, parsed as WebSocketMessage]);
                if (parsed?.m === "system.push" && parsed.v === "1.0") {
                    handleReceiveMsg(parsed.p);
                }
                if (parsed?.m === "chat.forbid") {
                    setListChannelBans(parsed.p.channel_id)
                }
                if (parsed?.m === "user.auth") {
                    const person = parsed.p as UserInfo;
                    setUser(person);
                }
                if (parsed?.m === "channel.list") {
                    const {list} = parsed.p as ChatList;
                    setListChat(list);
                }
                if (parsed?.m === "user.update") {
                    try {
                        const p = parsed.p || {};
                        const partial: any = {};
                        if (typeof p.nick !== "undefined") partial.nick = p.nick;
                        if (typeof p.avatar !== "undefined") partial.avatar = p.avatar;
                        if (typeof p.level !== "undefined") partial.level = p.level;
                        if (Object.keys(partial).length > 0) {
                            dispatch(updateUserInfo(partial));
                        }
                        if (token) {
                            fetchAndUpdateBalance();
                        }
                    } catch (e) {
                    }
                }

            } catch (e) {
                console.log(e);
            }
        },
        onError: (event) => {
            console.error("❌ WebSocket error:", event);
        },
        onClose: (event) => {
            console.warn("🔌 WebSocket closed:", {
                code: event.code,
                reason: event.reason,
                wasClean: event.wasClean,
            });
        },
        shouldReconnect: () => true,
        reconnectAttempts: 10,
        reconnectInterval: 3000,
        retryOnError: true,
    });

    const startHeartbeat = useCallback(() => {
        stopHeartbeat();
        if (readyState === ReadyState.OPEN) {
            heartbeatIntervalRef.current = setInterval(() => {
                if (readyState === ReadyState.OPEN) {
                    sendMessage(heartbeatMessage);
                }
            }, heartbeatInterval);
        }
    }, [readyState, heartbeatInterval, sendMessage, heartbeatMessage]);

    const stopHeartbeat = useCallback(() => {
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (readyState === ReadyState.OPEN) {
            startHeartbeat();
        } else {
            setUser(undefined);
            stopHeartbeat();
        }

        return () => {
            stopHeartbeat();
        };
    }, [readyState, startHeartbeat, stopHeartbeat]);

    useEffect(() => {
        const loginWebsocket = async () => {
            const params = {
                m: "user.auth",
                p: {
                    token: token,
                },
            };
            sendMessage(JSON.stringify(params));
        };

        if (readyState === ReadyState.OPEN && token) {
            loginWebsocket();
        }
    }, [readyState, sendMessage, token]);

    useEffect(() => {
        console.log('ws init')
    }, [])

    // console.log('readyState', readyState)

    const isConnection = useMemo(() => readyState === ReadyState.OPEN, [readyState]);

    const contextValue: WebSocketContextType = {
        sendMessage,
        lastJsonMessage: lastJsonMsg,
        readyState,
        wsUrl,
        setWsUrl,
        userInfo: user,
        isConnection: isConnection,
        messageQueue,
        setMessageQueue,
        listChat: listChat,
        listChannelBans
    };


    if (!isClient) {
        return children
    }

    return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
};

export const useWebSocketContext = (): WebSocketContextType => {
    const isClient = typeof window !== "undefined";

    if (!isClient) {
        return {
            sendMessage: () => {},
            lastJsonMessage: null,
            readyState: ReadyState.CLOSED,
            wsUrl: null,
            setWsUrl: () => {},
            isConnection: false,
            userInfo: undefined,
            messageQueue: [],
            setMessageQueue: () => {},
            listChat: [],
            listChannelBans: []
        }
    }

    const context = useContext(WebSocketContext);
    if (context === null) {
        throw new Error("useWebSocketContext must be used within a WebSocketProvider");
    }
    return context;
};
